import { formatPriceRange } from "dash-common";
import {
  Flex,
  Text,
  Icon,
  TooltipWrapper,
  formatBedroomsRangeValues,
  formatBathroomsRangeValues,
  Badge,
  IconKeys,
} from "dash-ui";
import { useTranslation } from "react-i18next";
import { useTheme } from "styled-components";

import { MinMaxRangePayload, Property } from "gql/generated.graphql";
import { useMemo } from "react";
import { DefaultTFuncReturn } from "i18next";

const MapPropertyUnitsRow = ({
  property,
  smallScale,
  tooltip = true,
}: {
  property: Property;
  smallScale?: boolean;
  tooltip?: boolean;
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const data = useMemo(() => {
    return {
      bedsData: {
        text:
          formatBedroomsRangeValues(property?.averageBedroomsNumberValue, {
            t,
            isNullable: property.averageBedroomsNumberValue?.min === 0,
          }) || t("common:labels:noValue"),
        available: !!formatBedroomsRangeValues(
          property?.averageBedroomsNumberValue,
          {
            t,
            isNullable: property.averageBedroomsNumberValue?.min === 0,
          }
        ),
      },
      bathsData: {
        text:
          formatBathroomsRangeValues(property?.averageBathroomsNumberValue, {
            t,
            isNullable: property.averageBathroomsNumberValue?.min === 0,
          }) || t("common:labels:noValue"),
        available: !!formatBathroomsRangeValues(
          property?.averageBathroomsNumberValue,
          {
            t,
            isNullable: property.averageBathroomsNumberValue?.min === 0,
          }
        ),
      },
      priceData: {
        text:
          formatPriceRange(
            property.priceRange?.min || 0,
            property.priceRange?.max || 0,
            {
              shortMin: true,
              shortMax: true,
              prefix: t("common:labels:from") || "",
            },
            {
              maximumFractionDigits: 2,
            }
          ) || t("common:labels:noValue"),
        available: !!formatPriceRange(
          property.priceRange?.min || 0,
          property.priceRange?.max || 0,
          {
            shortMin: true,
            shortMax: true,
            prefix: t("common:labels:from") || "",
          },
          {
            maximumFractionDigits: 2,
          }
        ),
      },
    };
  }, [property]);

  const badgeStyle = useMemo(
    () =>
      !smallScale
        ? {
            h: 32,
            mt: 1.125,
          }
        : {},
    [smallScale]
  );

  const renderContent = (
    text: number | DefaultTFuncReturn,
    iconName: IconKeys
  ) => (
    <Flex mr={3} alignItems="center" overflow="hidden">
      <Flex alignItems="center">
        <Icon name={iconName} mr={1} size={24} color={theme.colors.n80} />
      </Flex>
      <Text smplus semibold ellipsis>
        {text}
      </Text>
    </Flex>
  );

  if (property.projectStatus === "PROPOSED") {
    return (
      <Flex alignItems="center" w="100%" {...badgeStyle}>
        <Badge
          title="Proposed"
          bgColor={theme.colors.setOpacity(theme.colors.b30, 70)}
          titleColor={theme.colors.b50}
          sm
          py={1}
          mr={2}
          square={true}
        />
      </Flex>
    );
  }

  if (property.projectStatus === "COMING_SOON") {
    return (
      <Flex alignItems="center" w="100%" {...badgeStyle}>
        <Badge
          title={t("common:labels:comingSoon")}
          bgColor={theme.colors.setOpacity(theme.colors.b30, 70)}
          titleColor={theme.colors.r50}
          sm
          py={1}
          mr={2}
          square={true}
        />
      </Flex>
    );
  }

  if (property.projectStatus === "SOLD_OUT") {
    return (
      <Flex alignItems="center" w="100%" {...badgeStyle}>
        <Badge
          title={t("common:labels:soldOut")}
          bgColor={"#FF6969"}
          titleColor={"#FFF"}
          sm
          py={1}
          mr={2}
          square={true}
        />
      </Flex>
    );
  }

  return (
    <Flex alignItems="center" mt={1} w="100%">
      {tooltip ? (
        <>
          <TooltipWrapper
            content={formatPriceRange(
              property.priceRange?.min || 0,
              property.priceRange?.max || 0,
              {
                shortMin: false,
                shortMax: false,
                prefix: t("common:labels:from") || "",
              }
            )}
          >
            <Flex overflow="hidden" mr={3}>
              <Text smplus semibold ellipsis color={theme.colors.g20}>
                {data.priceData.text}
              </Text>
            </Flex>
          </TooltipWrapper>
          <TooltipWrapper
            content={formatBedroomsRangeValues(
              property.averageBedroomsNumberValue as MinMaxRangePayload
            )}
          >
            {renderContent(data.bedsData.text, "bedroom")}
          </TooltipWrapper>
          <TooltipWrapper
            content={formatBathroomsRangeValues(
              property.averageBathroomsNumberValue as MinMaxRangePayload
            )}
          >
            {renderContent(data.bathsData.text, "bathroom")}
          </TooltipWrapper>
        </>
      ) : (
        <>
          <Flex overflow="hidden" mr={3}>
            <Text smplus semibold ellipsis color={theme.colors.g20}>
              {data.priceData.text}
            </Text>
          </Flex>
          {renderContent(data.bedsData.text, "bedroom")}
          {renderContent(data.bathsData.text, "bathroom")}
        </>
      )}
    </Flex>
  );
};

export default MapPropertyUnitsRow;
